import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeSVG } from 'qrcode.react';
import { fetchDestinationPoints, reserveBoxIds } from '../../services/inboundService';

interface PrintLabelsModalProps {
  open: boolean;
  onClose: () => void;
  selectedInboundIds: number[];
}

interface DestinationEntry {
  name: string;
  quantity: number;
}

interface LabelEntry {
  destination: string;
  boxId: string;
}

// Label: 100×75 mm. QR = 45 mm (≥45% width). Padding 3 mm on all edges. Gap 3 mm.
// Layout: [pad 3mm][QR 45mm][gap 3mm][text 46mm][pad 3mm] = 100mm
const LABEL_W_MM = 100;
const LABEL_H_MM = 75;
const QR_SIZE_MM = 45;
const PREVIEW_SCALE = 2; // px per mm

/**
 * If name > 25 chars, split at the first hyphen or underscore after position 5.
 * Returns 1 or 2 parts.
 */
function splitDestName(name: string): [string] | [string, string] {
  if (name.length <= 25) return [name];
  const idx = name.slice(5).search(/[-_]/);
  if (idx === -1) return [name];
  const pos = 5 + idx;
  return [name.slice(0, pos + 1).trim(), name.slice(pos + 1).trim()];
}

export const PrintLabelsModal: React.FC<PrintLabelsModalProps> = ({ open, onClose, selectedInboundIds }) => {
  const [step, setStep] = useState<'select' | 'preview'>('select');
  const [destinations, setDestinations] = useState<DestinationEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [labels, setLabels] = useState<LabelEntry[]>([]);
  // Refs to visible SVG elements — serialized for print via XMLSerializer (avoids Chrome 109 canvas bug)
  const svgRefs = useRef<(SVGSVGElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    setStep('select');
    setLabels([]);
    loadDestinations();
  }, [open, selectedInboundIds]);

  const loadDestinations = async () => {
    setIsLoading(true);
    try {
      const results = await Promise.all(selectedInboundIds.map((id) => fetchDestinationPoints(id)));
      const uniqueNames = Array.from(new Set(results.flat()));
      uniqueNames.unshift('ОБЩЕЕ');
      setDestinations(uniqueNames.map((name) => ({ name, quantity: 1 })));
    } catch {
      setDestinations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityChange = useCallback((index: number, value: number) => {
    setDestinations((prev) => prev.map((d, i) => (i === index ? { ...d, quantity: Math.min(9, Math.max(0, value)) } : d)));
  }, []);

  const handleIncrementAll = useCallback(() => {
    setDestinations((prev) => prev.map((d) => ({ ...d, quantity: Math.min(9, d.quantity + 1) })));
  }, []);

  const handleDecrementAll = useCallback(() => {
    setDestinations((prev) => prev.map((d) => ({ ...d, quantity: Math.max(0, d.quantity - 1) })));
  }, []);

  const totalCount = destinations.reduce((sum, d) => sum + d.quantity, 0);

  const handleGenerate = async () => {
    if (totalCount === 0) return;
    setIsGenerating(true);
    try {
      const boxIds = await reserveBoxIds(totalCount);
      const generated: LabelEntry[] = [];
      let idIndex = 0;
      for (const dest of destinations) {
        for (let i = 0; i < dest.quantity; i++) {
          generated.push({ destination: dest.name, boxId: boxIds[idIndex++] });
        }
      }
      svgRefs.current = new Array(generated.length).fill(null);
      setLabels(generated);
      setStep('preview');
    } catch (_e) {
      // reserve-ids request failed
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    const serializer = new XMLSerializer();
    const labelPages = labels
      .map((label, i) => {
        const parts = splitDestName(label.destination);
        const twoLine = parts.length === 2;
        const svgEl = svgRefs.current[i];
        const svgString = svgEl ? serializer.serializeToString(svgEl) : '';
        const destHtml = twoLine ? `${parts[0]}<br/>${parts[1]}` : parts[0];
        return `
        <div class="label">
          <div class="qr-wrap">${svgString}</div>
          <div class="label-info">
            <div class="label-id">${label.boxId}</div>
            <div class="label-dest">${destHtml}</div>
          </div>
        </div>`;
      })
      .join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <title>Этикетки</title>
    <style>
      @page {
        size: ${LABEL_W_MM}mm ${LABEL_H_MM}mm;
        margin: 0;
      }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; background: #fff; }
      .label {
        width: ${LABEL_W_MM}mm;
        height: ${LABEL_H_MM}mm;
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 3mm;
        gap: 3mm;
        page-break-after: always;
        overflow: hidden;
      }
      .label:last-child { page-break-after: avoid; }
      .qr-wrap {
        width: ${QR_SIZE_MM}mm;
        height: ${QR_SIZE_MM}mm;
        flex-shrink: 0;
      }
      .qr-wrap svg {
        width: 100% !important;
        height: 100% !important;
      }
      .label-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
      }
      .label-id {
        font-size: 14pt;
        font-weight: bold;
        letter-spacing: 1px;
        line-height: 1.2;
        word-break: break-all;
      }
      .label-dest {
        font-size: 14pt;
        font-weight: bold;
        margin-top: 2mm;
        line-height: 1.4;
        word-break: break-word;
      }
    </style>
  </head>
  <body>${labelPages}</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  const handleClose = () => {
    setStep('select');
    setLabels([]);
    onClose();
  };

  const previewW = LABEL_W_MM * PREVIEW_SCALE;
  const previewH = LABEL_H_MM * PREVIEW_SCALE;
  const qrPx = QR_SIZE_MM * PREVIEW_SCALE;
  // pad 6px + qr 90px + gap 6px + text + pad 6px = 200px → text = 92px
  const textPx = previewW - 6 - qrPx - 6 - 6;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      {step === 'select' && (
        <>
          <DialogTitle>Распечатать этикетки</DialogTitle>
          <DialogContent>
            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box display="flex" alignItems="center" mb={1} mt={1}>
                  <Box flex={1} />
                  <Box display="flex" alignItems="center" gap={1} minWidth={160} justifyContent="center">
                    <Typography variant="caption" color="text.secondary">
                      Кол-во
                    </Typography>
                    <IconButton size="small" onClick={handleDecrementAll} color="primary">
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleIncrementAll} color="primary">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                {destinations.length === 0 ? (
                  <Typography color="text.secondary">Пункты назначения не найдены</Typography>
                ) : (
                  destinations.map((dest, index) => (
                    <Box key={dest.name} display="flex" alignItems="center" py={0.75} borderBottom="1px solid #f0f0f0">
                      <Typography flex={1}>{dest.name}</Typography>
                      <Box minWidth={160} display="flex" justifyContent="center">
                        <TextField
                          type="number"
                          size="small"
                          value={dest.quantity}
                          onChange={(e) => handleQuantityChange(index, parseInt(e.target.value, 10) || 0)}
                          inputProps={{ min: 0, max: 9, style: { width: 52, textAlign: 'center' } }}
                        />
                      </Box>
                    </Box>
                  ))
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 2 }}>
              Итого: {totalCount}
            </Typography>
            <Button onClick={handleClose}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isGenerating || totalCount === 0 || isLoading}
              startIcon={isGenerating ? <CircularProgress size={16} /> : null}
            >
              {isGenerating ? 'Формирование...' : 'Сформировать'}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'preview' && (
        <>
          <DialogTitle>
            Предпросмотр этикеток ({labels.length} шт.) — {LABEL_W_MM}×{LABEL_H_MM} мм
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }}>
              {labels.map((label, i) => {
                const parts = splitDestName(label.destination);
                const twoLine = parts.length === 2;
                return (
                  <Box
                    key={i}
                    sx={{
                      width: previewW,
                      height: previewH,
                      border: '1px solid #bbb',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      p: '6px',
                      gap: '6px',
                      bgcolor: '#fff',
                      boxShadow: '1px 1px 4px rgba(0,0,0,0.15)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ flexShrink: 0, width: qrPx, height: qrPx }}>
                      <QRCodeSVG
                        value={`${label.destination}|${label.boxId}`}
                        size={qrPx}
                        ref={(el) => {
                          svgRefs.current[i] = el;
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: textPx,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: 14,
                          lineHeight: 1.2,
                          letterSpacing: '0.5px',
                          wordBreak: 'break-all',
                        }}
                      >
                        {label.boxId}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 17, mt: '4px', lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {parts[0]}
                        {twoLine && (
                          <>
                            <br />
                            {parts[1]}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep('select')}>Назад</Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
              Печать
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
